import { useState } from 'react';
import { Search, Package, Truck, CheckCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderTrackingPage = () => {
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);

  const handleTrack = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.get(`${API}/orders/track/${orderNumber}`, {
        params: { email }
      });
      setOrder(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Order not found');
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusStep = (status) => {
    const steps = ['pending', 'confirmed', 'packed', 'shipped', 'delivered'];
    return steps.indexOf(status);
  };

  return (
    <div className="pt-32 pb-24" data-testid="order-tracking-page">
      <div className="chaleur-container">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <p className="caption text-primary mb-4">Order Tracking</p>
            <h1 className="text-3xl md:text-4xl font-light mb-6">Track Your Order</h1>
            <p className="text-muted-foreground">
              Enter your order number and email to see the latest status of your shipment.
            </p>
          </div>

          {/* Tracking Form */}
          <form onSubmit={handleTrack} className="space-y-6 mb-12">
            <div>
              <Label htmlFor="orderNumber" className="text-sm mb-2 block">Order Number</Label>
              <Input
                id="orderNumber"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="e.g. CHL-20240115-ABC123"
                className="input-chaleur"
                required
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-sm mb-2 block">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email used for order"
                className="input-chaleur"
                required
              />
            </div>
            <Button type="submit" className="btn-chaleur-primary w-full" disabled={loading}>
              <Search className="w-4 h-4 mr-2" />
              {loading ? 'Searching...' : 'Track Order'}
            </Button>
          </form>

          {/* Order Status */}
          {order && (
            <div className="border border-border p-8">
              <div className="flex justify-between items-start mb-8">
                <div>
                  <p className="text-sm text-muted-foreground">Order Number</p>
                  <p className="text-xl font-medium">{order.order_number}</p>
                </div>
                <span className={`px-3 py-1 text-sm ${
                  order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                  order.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                  'bg-secondary text-muted-foreground'
                }`}>
                  {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                </span>
              </div>

              {/* Progress Steps */}
              <div className="relative mb-8">
                <div className="flex justify-between">
                  {['Confirmed', 'Packed', 'Shipped', 'Delivered'].map((step, idx) => {
                    const isComplete = getStatusStep(order.status) >= idx;
                    const isCurrent = getStatusStep(order.status) === idx;
                    return (
                      <div key={step} className="flex flex-col items-center relative z-10">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isComplete ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'
                        }`}>
                          {idx === 0 && <Package className="w-5 h-5" />}
                          {idx === 1 && <Package className="w-5 h-5" />}
                          {idx === 2 && <Truck className="w-5 h-5" />}
                          {idx === 3 && <CheckCircle className="w-5 h-5" />}
                        </div>
                        <p className={`text-xs mt-2 ${isCurrent ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                          {step}
                        </p>
                      </div>
                    );
                  })}
                </div>
                <div className="absolute top-5 left-0 right-0 h-0.5 bg-secondary -z-0">
                  <div 
                    className="h-full bg-primary transition-all"
                    style={{ width: `${(getStatusStep(order.status) / 3) * 100}%` }}
                  />
                </div>
              </div>

              {/* Tracking Number */}
              {order.tracking_number && (
                <div className="p-4 bg-secondary/50 mb-6">
                  <p className="text-sm text-muted-foreground mb-1">Tracking Number</p>
                  <p className="font-mono">{order.tracking_number}</p>
                  {order.courier && (
                    <p className="text-sm text-muted-foreground mt-1">Via {order.courier}</p>
                  )}
                </div>
              )}

              {/* Items */}
              <div>
                <h3 className="font-medium mb-4">Order Items</h3>
                <div className="space-y-3">
                  {order.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>{item.product_name} × {item.quantity}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-border text-sm text-muted-foreground">
                <Clock className="w-4 h-4 inline mr-2" />
                Order placed on {new Date(order.created_at).toLocaleDateString('en-US', {
                  year: 'numeric', month: 'long', day: 'numeric'
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderTrackingPage;
